import { useState, useLayoutEffect, type RefObject } from 'react';
import noop from 'lodash/noop';
import { useVirtual } from 'react-virtual';
import { useDebouncedCallback } from '@atlassian/jira-platform-use-debounce';
import type { Meta, EmptyMeta } from './types';

const DEFAULT_OVERSCAN = 2;

export const useLayoutReady = (): boolean => {
	const [layoutReady, setLayoutReady] = useState(false);

	// react-virtual re-calculates elements when layout is ready
	// we need to make sure we don't wastefully render columns before that
	useLayoutEffect(() => {
		setLayoutReady(true);
	}, []);

	return layoutReady;
};

const emptyMeta = {
	virtualItems: [],
	totalSize: 0,
	scrollToIndex: noop,
	onBoardCardResize: noop,
} as const;

/** @deprecated Remove when jwm.board-awesome-enhancements is cleaned up */
export const useVirtualItems = ({
	scrollRef,
	size,
	estimateSize,
	horizontal,
	overscan = DEFAULT_OVERSCAN,
	useObserver,
	rangeExtractor,
	keyExtractor,
}: {
	scrollRef: RefObject<HTMLElement>;
	size: number;
	estimateSize?: (index: number) => number;
	horizontal?: boolean;
	overscan?: number;
	useObserver?: () => {
		width: number;
		height: number;
	};
	/**
	 * To use this property, please use the new v3 version of react-virtual
	 * exported from `@atlassian/jira-react-virtual`.
	 */
	rangeExtractor?: never;
	keyExtractor?: (index: number) => string | number;
}): Meta | EmptyMeta => {
	const layoutReady = useLayoutReady();

	// We want to trigger a re-render to correctly display the updated height of board cards
	const [, setShouldRecalculateVirtualization] = useState<number>(0);

	const [onBoardCardResize] = useDebouncedCallback(() => {
		setShouldRecalculateVirtualization((prevCount) => prevCount + 1);
	}, 250);

	const meta = useVirtual({
		size,
		horizontal,
		parentRef: scrollRef,
		estimateSize,
		overscan,
		useObserver,
		rangeExtractor,
		keyExtractor,
	});

	return {
		...(layoutReady ? meta : emptyMeta),
		onBoardCardResize,
	};
};
